import React from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import theme from "styles/theme"
import Container from "components/Container"
import BlockHeader from "components/BlockHeader"
// import ScrollIndicator from "components/ScrollIndicator"
import CTAButton from "components/buttons/CTAButton"
import AccordionList from "components/AccordionList"
import StatsBlock from "components/blocks/StatsBlock"
import ResourceBlock from "components/blocks/ResourceBlock"
import TestimonialCard from "components/cards/TestimonialCard"
import TextBlock from "components/TextBlock"
import TextWithSidebar from "components/blocks/TextWithSidebar"
import IconCards from "components/IconCards"
import MapBlock from "components/blocks/MapBlock"
import ChartBlock from "components/blocks/ChartBlock"
import CrossLinks from "components/CrosslinksContent"
import FeatureBlock from "components/blocks/FeatureBlock"

const FlexibleContentBlock = ({
  context,
  id,
  label,
  title,
  titleUrl,
  flexContent,
  // scrollIndicator,
}) => {
  const { copy, richCopy } = flexContent[0]
  let nthConsecutiveOfType = 0
  return (
    <Container
      id={id}
      wrapperStyles={{
        padding: "84px 74px 98px",
        [theme.tablet]: {
          paddingTop: 54,
          paddingBottom: 67,
        },
        [theme.mobile]: { padding: "39px 40px 69px" },
        [theme.smallMobilePortrait]: { padding: "40px 20px 60px" },
      }}
    >
      <BlockHeader label={label} title={title} titleUrl={titleUrl} />
      {copy && <div css={copyStyles}>{copy}</div>}
      {richCopy && <TextBlock css={copyStyles}>{richCopy}</TextBlock>}

      {flexContent.map(({ copy, richCopy, ...flexContentProps }, index) => {
        // NOTE Due to GraphQL type errors, aliases were needed.
        if (typeof flexContentProps._type === 'undefined') {
          flexContentProps._type = flexContentProps.featureBlockType
        }
        // NOTE The `nthConsecutiveOfType` from within the `context` object did not yield
        // an accurate/reset count. Thus, a fresh/cleared count was needed.
        if (index > 0 && flexContent[index]._type === flexContent[index - 1]._type) {
          nthConsecutiveOfType++
        } else {
          nthConsecutiveOfType = 1
        }
        return (
          <FlexContent
            key={`flexcontent${index}`}
            context={context}
            nthConsecutiveOfType={nthConsecutiveOfType}
            {...flexContentProps}
          />
        )
      })}

      {/* {scrollIndicator && (
        <ScrollIndicator
          fragment={context.nextSectionId}
          css={{
            marginTop: 23,
            transform: "translateY(45px)",
            [theme.tablet]: { marginTop: 12 },
            [theme.mobile]: { marginTop: 0 },
          }}
        >
          {scrollIndicator}
        </ScrollIndicator>
      )} */}
    </Container>
  )
}

export default FlexibleContentBlock

FlexibleContentBlock.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  flexContent: PropTypes.arrayOf(
    PropTypes.shape({
      copy: PropTypes.string,
      richCopy: PropTypes.array,
    })
  ).isRequired,
  // scrollIndicator: PropTypes.string,
}

const copyStyles = {
  ...theme.bodyCopy,
  maxWidth: 746,
  textAlign: "center",
  margin: "34px auto 0",
  [theme.tablet]: { marginTop: 24 },
}

const FlexContent = ({ _type, context, nthConsecutiveOfType, ...props }) => {
  switch (_type) {
    case "simpleFlexContent":
      return <SimpleFlexContent {...props} />
    case "accordionContent":
      return <AccordionList {...props} />
    case "statsGrid":
      return <StatsBlock {...props} />
    case "resourceGrid":
      return <ResourceBlock {...props} />
    case "textWithSidebar":
      return <TextWithSidebar {...props} />
    case "iconCards":
      return <IconCards {...props} />
    case "testimonial":
      return <TestimonialCard {...props} />
    case "mapContent":
      return <MapBlock {...props} />
    case "chartGroup":
      return <ChartBlock {...props} />
    case "crosslinksContent":
      return <CrossLinks {...props} />
    case "featureBlock":
      return (
        <div
          css={{
            width: '100vw',
            // NOTE Assigning .01px vertical padding was needed for a 10px border to show up. Not sure why.
            marginTop: nthConsecutiveOfType === 1 ? '80px' : null,
            paddingTop: nthConsecutiveOfType === 1 ? '.01px' : null,
            paddingBottom: '.01px',
            marginBottom: '-10px',
            marginLeft: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: theme.white,
          }}
        >
          <FeatureBlock
            {...props}
            // NOTE Due to GraphQL type errors, aliases were needed.
            copy={props.featureBlockCopy}
            context={{
              ...context,
              nthConsecutiveOfType,
            }}
          />
        </div>
      )
    default:
      return null
  }
}

FlexContent.propTypes = {
  _type: PropTypes.string.isRequired,
}

const SimpleFlexContent = ({ action }) => {
  const href = action?.href ? action.href.trim() : ''
  const text = action?.text ? action.text.trim() : ''
  return href && text && (
    <div css={{ textAlign: "center" }}>
      <CTAButton href={href} targetBlank={action.targetBlank} css={{ marginTop: 42 }}>
        {text}
      </CTAButton>
    </div>
  )
}

SimpleFlexContent.propTypes = {
  action: PropTypes.shape({
    text: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
  }),
}

export const query = graphql`
  fragment FlexibleContentBlock on SanityFlexibleContent {
    _key
    label
    title
    titleUrl
    # scrollIndicator
    flexContent: content {
      ...AccordionContent

      ... on SanitySimpleFlexContent {
        _type
        copy
        action {
          ...CTAButton
        }
      }

      ... on SanityResourceGrid {
        _type
        richCopy: _rawRichCopy
        gridSubhead
        resources {
          title
          type
          url
        }
      }

      ... on SanityStatsGrid {
        _type
        copy
        stats {
          figure
          description
          footnote
        }
      }

      ... on SanityTextWithSidebar {
        _type
        title
        primaryContent: _rawPrimaryContent
        sidebarContent: _rawSidebarContent
        actions {
          ...CTAButton
          ...CTAIconButton
        }
      }

      ... on SanityIconCards {
        _type
        reverseDesktopLayout
        title
        description: _rawDescription
        cards {
          title
          content
          icon {
            ...Icon
          }
        }
      }

      ... on SanityTestimonial {
        _type
        quote
        attribution
        image {
          ...Image
        }
      }

      ... on SanityMapContent {
        _type
        copy
        markers {
          resource
          url
        }
        mobileImage {
          ...Image
        }
        hqUrl
        action {
          ...CTAButton
        }
      }

      ... on SanityChartGroup {
        _type
        title
        rows {
          ...PieChart
          ...ColumnChart
          ...ImageChart
        }
      }

      ... on SanityCrosslinksContent {
        _type
        id: _key
        label
        title
        links: entries {
          _type
          label
          title
          link
          icon {
            ...Icon
          }
        }
      }

      ... on SanityFeatureBlock {
        featureBlockType: _type
        label
        title
        featureBlockCopy: _rawCopy
        image {
          ...Image
        }
        actions {
          ...CTAButton
          ...CTACallout
          ...CTAIconButton
        }
      }

    }
  }
`
